import { useMemo } from "react";
import { Box, BoxProps } from "@mui/material";
import DOMPurify from "dompurify";

const ALLOWED_TAGS = [
  "b",
  "strong",
  "i",
  "em",
  "u",
  "br",
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
];

type HtmlContentProps = Omit<BoxProps, "children" | "dangerouslySetInnerHTML"> & {
  /** Untrusted HTML markup. Sanitized to a strict tag allowlist before rendering. */
  html: string;
};

/**
 * Renders a string of untrusted HTML safely.
 * Only simple formatting tags survive; all attributes are stripped.
 */
const HtmlContent: React.FC<HtmlContentProps> = ({ html, sx, ...boxProps }) => {
  const sanitized = useMemo(
    () => DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR: [] }),
    [html],
  );

  return (
    <Box
      {...boxProps}
      sx={[
        {
          overflowWrap: "anywhere",
          "& p": { my: 0.5 },
          "& p:first-of-type, & h1:first-of-type, & h2:first-of-type, & h3:first-of-type, & h4:first-of-type, & h5:first-of-type, & h6:first-of-type":
            { mt: 0 },
          "& p:last-child, & ul:last-child, & ol:last-child": { mb: 0 },
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            fontSize: "1rem",
            fontWeight: 600,
            mt: 1.5,
            mb: 0.5,
          },
          "& ul, & ol": { my: 0.5, pl: 3 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};

export default HtmlContent;
